import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, Crown, Spinner, WarningCircle } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useSubscription } from "../context/SubscriptionContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useSubscription();
  const [state, setState] = useState("checking"); // checking | success | pending | error
  const sessionId = params.get("session_id");
  const isTopup = params.get("type") === "topup";
  const gaFired = useRef(false);

  const poll = useCallback(async (attempt = 0) => {
    if (!sessionId) { setState("error"); return; }
    if (attempt >= 8) { setState("pending"); return; }
    try {
      const res = await api.get(`/payments/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        // GA4 — fire pro_upgrade exactly once per fulfilled subscription
        // payment. Guarded by:
        //   1. gaFired ref (prevents double-fire from React re-renders / polls)
        //   2. payment_status === "paid" (never on page load / pending / failed)
        //   3. !isTopup + type !== "topup" (top-ups are credit packs, not plan upgrades)
        //   4. plan_id present (only for genuine subscription purchases)
        if (!gaFired.current && !isTopup && res.data.type !== "topup" && res.data.plan_id) {
          gaFired.current = true;
          const evtParams = {
            value: typeof res.data.amount === "number" ? res.data.amount : undefined,
            currency: res.data.currency || "USD",
            plan: res.data.plan_name || res.data.plan_id,
            plan_id: res.data.plan_id,
            transaction_id: sessionId,
          };
          if (typeof window !== "undefined") {
            if (typeof window.gtag === "function") {
              window.gtag("event", "pro_upgrade", evtParams);
            } else if (Array.isArray(window.dataLayer)) {
              window.dataLayer.push(["event", "pro_upgrade", evtParams]);
            }
          }
        }
        await refresh();
        setState("success");
        // Auto-redirect to AI Pro Studio so the user lands in the workspace
        // immediately after payment (per the PLG post-purchase spec). The
        // 1.6s delay lets them see the success confirmation first, then hands
        // them off — button is available as an explicit fallback.
        setTimeout(() => { navigate("/ai-pro-studio", { replace: true }); }, 1600);
        return;
      }
      if (res.data.status === "expired") { setState("error"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch (e) {
      setTimeout(() => poll(attempt + 1), 2000);
    }
  }, [sessionId, refresh, isTopup, navigate]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white px-4">
      <div className="w-full max-w-md text-center rounded-2xl border border-white/10 bg-[#101013] p-10" data-testid="payment-success-page">
        {state === "checking" && (
          <>
            <Spinner size={44} className="text-blue-500 animate-spin mx-auto mb-5" />
            <h1 className="font-display text-2xl font-black mb-2">Confirming your payment…</h1>
            <p className="text-sm text-zinc-400">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <div className="h-16 w-16 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-5">
              <CheckCircle weight="fill" size={40} className="text-emerald-400" />
            </div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-400/15 text-amber-300 text-xs font-semibold mb-3">
              <Crown weight="fill" size={13} /> {isTopup ? "Credits added" : "Pro unlocked"}
            </div>
            <h1 className="font-display text-2xl font-black mb-2">You're all set!</h1>
            <p className="text-sm text-zinc-400 mb-6">{isTopup ? "Your credit pack has been added to your wallet — they never expire. Start creating!" : "Welcome to FlyerStudio Pro. Watermark-free exports, PDF, share links and more are now yours."}</p>
            <button onClick={() => navigate("/ai-pro-studio")} data-testid="success-dashboard-btn" className="inline-flex items-center justify-center h-11 px-6 rounded-full bg-blue-600 hover:bg-blue-500 font-semibold transition-colors">
              Go to AI Pro Studio
            </button>
          </>
        )}
        {(state === "pending" || state === "error") && (
          <>
            <div className="h-16 w-16 rounded-full bg-amber-500/15 flex items-center justify-center mx-auto mb-5">
              <WarningCircle weight="fill" size={40} className="text-amber-400" />
            </div>
            <h1 className="font-display text-2xl font-black mb-2">{state === "pending" ? "Still processing" : "Hmm, something went wrong"}</h1>
            <p className="text-sm text-zinc-400 mb-6">
              {state === "pending"
                ? "Your payment is taking longer than usual. If you were charged, your Pro access will activate automatically."
                : "We couldn't confirm this payment. If you were charged, contact support and we'll sort it out."}
            </p>
            <button onClick={() => navigate("/dashboard")} className="inline-flex items-center justify-center h-11 px-6 rounded-full bg-white/10 hover:bg-white/20 font-semibold transition-colors">
              Back to dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
}
